import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Package, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { getGarmentTypes } from '@/pages/parametrizacion/garmentTypes/Services/garmentTypes.services';
import { registrarEntradaInventario } from '@/services/movimientosLenceria.service';

const RegistrarEntradaModal = ({ isOpen, onClose, onSuccess, user }) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [garmentTypes, setGarmentTypes] = useState([]);
  const [isLoadingGarmentTypes, setIsLoadingGarmentTypes] = useState(false);
  
  const [formData, setFormData] = useState({
    numero_factura: '',
    consecutivo: '',
    observaciones: '',
    prendas: []
  });

  const [newPrenda, setNewPrenda] = useState({
    tipo_prenda_id: '',
    cantidad: 1
  });

  // Cargar tipos de prenda
  useEffect(() => {
    if (isOpen) {
      loadGarmentTypes();
    }
  }, [isOpen]);

  const loadGarmentTypes = async () => {
    setIsLoadingGarmentTypes(true);
    try {
      const response = await getGarmentTypes({ 
        page: 1, 
        limit: 1000,
        query: ''
      });
      setGarmentTypes(response.data || []);
    } catch (error) {
      console.error('Error al cargar tipos de prenda:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los tipos de prenda',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingGarmentTypes(false);
    }
  };

  const handleAddPrenda = () => {
    if (!newPrenda.tipo_prenda_id || newPrenda.cantidad <= 0) {
      toast({
        title: 'Error',
        description: 'Selecciona una prenda y una cantidad válida',
        variant: 'destructive'
      });
      return;
    }

    const garmentType = garmentTypes.find(g => g._id === newPrenda.tipo_prenda_id);
    if (!garmentType) return;

    // Verificar si ya existe
    const existingIndex = formData.prendas.findIndex(p => p.tipo_prenda_id === newPrenda.tipo_prenda_id);
    
    if (existingIndex >= 0) {
      // Sumar cantidad
      const updatedPrendas = [...formData.prendas];
      updatedPrendas[existingIndex].cantidad += parseInt(newPrenda.cantidad);
      setFormData({ ...formData, prendas: updatedPrendas });
    } else {
      // Agregar nueva
      setFormData({
        ...formData,
        prendas: [
          ...formData.prendas,
          {
            tipo_prenda_id: garmentType._id,
            tipo_prenda_nombre: garmentType.nombre,
            tipo_prenda_peso: garmentType.peso,
            cantidad: parseInt(newPrenda.cantidad)
          }
        ]
      });
    }

    // Reset
    setNewPrenda({ tipo_prenda_id: '', cantidad: 1 });
  };

  const handleRemovePrenda = (index) => {
    const updatedPrendas = formData.prendas.filter((_, i) => i !== index);
    setFormData({ ...formData, prendas: updatedPrendas });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.prendas.length === 0) {
      toast({
        title: 'Error',
        description: 'Debes agregar al menos una prenda',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.numero_factura && !formData.consecutivo) {
      toast({
        title: 'Error',
        description: 'Debes ingresar un número de factura o consecutivo',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      await registrarEntradaInventario({
        ...formData,
        usuario_id: user.id,
        usuario_nombre: user.name
      });

      toast({
        title: 'Éxito',
        description: 'Entrada registrada correctamente',
      });

      // Reset form
      setFormData({
        numero_factura: '',
        consecutivo: '',
        observaciones: '',
        prendas: []
      });

      onSuccess();
    } catch (error) {
      console.error('Error al registrar entrada:', error);
      toast({
        title: 'Error',
        description: 'No se pudo registrar la entrada',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const totalPrendas = formData.prendas.reduce((sum, p) => sum + p.cantidad, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-purple-50 border-b border-purple-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Package className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Registrar Entrada de Inventario
              </h2>
              <p className="text-sm text-gray-600">
                Agrega prendas al inventario central
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            {/* Información de Factura */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="numero_factura">Número de Factura</Label>
                <Input
                  id="numero_factura"
                  value={formData.numero_factura}
                  onChange={(e) => setFormData({ ...formData, numero_factura: e.target.value })}
                  placeholder="Ej: FAC-001"
                />
              </div>
              <div>
                <Label htmlFor="consecutivo">Consecutivo</Label>
                <Input
                  id="consecutivo"
                  value={formData.consecutivo}
                  onChange={(e) => setFormData({ ...formData, consecutivo: e.target.value })}
                  placeholder="Ej: CONS-001"
                />
              </div>
            </div>

            {/* Agregar Prenda */}
            <div className="border-2 border-dashed border-purple-200 rounded-lg p-4 bg-purple-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Agregar Prenda
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label htmlFor="tipo_prenda">Tipo de Prenda</Label>
                  <select
                    id="tipo_prenda"
                    value={newPrenda.tipo_prenda_id}
                    onChange={(e) => setNewPrenda({ ...newPrenda, tipo_prenda_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    disabled={isLoadingGarmentTypes}
                  >
                    <option value="">Seleccionar prenda...</option>
                    {garmentTypes.map((garment) => (
                      <option key={garment._id} value={garment._id}>
                        {garment.nombre} {garment.peso ? `(${garment.peso}g)` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="cantidad">Cantidad</Label>
                  <div className="flex gap-2">
                    <Input
                      id="cantidad"
                      type="number"
                      min="1"
                      value={newPrenda.cantidad}
                      onChange={(e) => setNewPrenda({ ...newPrenda, cantidad: parseInt(e.target.value) || 1 })}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={handleAddPrenda}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de Prendas Agregadas */}
            {formData.prendas.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Prendas a Ingresar ({totalPrendas} unidades)
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Prenda</th>
                        <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Cantidad</th>
                        <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Peso Unit.</th>
                        <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.prendas.map((prenda, index) => (
                        <tr key={index} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">{prenda.tipo_prenda_nombre}</td>
                          <td className="px-4 py-3 text-sm text-center font-semibold">{prenda.cantidad}</td>
                          <td className="px-4 py-3 text-sm text-center text-gray-600">
                            {prenda.tipo_prenda_peso ? `${prenda.tipo_prenda_peso}g` : '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemovePrenda(index)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Observaciones */}
            <div>
              <Label htmlFor="observaciones">Observaciones</Label>
              <Textarea
                id="observaciones"
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                placeholder="Observaciones adicionales..."
                rows={3}
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || formData.prendas.length === 0}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {isLoading ? 'Registrando...' : 'Registrar Entrada'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RegistrarEntradaModal;
